import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { Loader, ChevronDown, ChevronUp, Copy, HelpCircle, AlertTriangle } from 'react-feather';

const api = process.env.REACT_APP_API_URL;

const CATEGORY_LABELS = {
  stretch_claim: { label: 'Stretch claim', cls: 'bg-red-50 text-red-700 border-red-200' },
  keyword_probe: { label: 'Keyword probe', cls: 'bg-violet-50 text-violet-700 border-violet-200' },
  gap: { label: 'Gap', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  experience_depth: { label: 'Depth check', cls: 'bg-blue-50 text-blue-700 border-blue-200' },
  behavioral: { label: 'Behavioral', cls: 'bg-gray-50 text-gray-600 border-gray-200' },
};

const SEVERITY_CLS = {
  high: 'bg-red-100 text-red-700',
  medium: 'bg-amber-100 text-amber-700',
  low: 'bg-gray-100 text-gray-600',
};

function QuestionCard({ q, index }) {
  const [open, setOpen] = useState(false);
  const cat = CATEGORY_LABELS[q.category] || CATEGORY_LABELS.behavioral;
  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden bg-white">
      <button onClick={() => setOpen(v => !v)} className="w-full text-left px-4 py-3 hover:bg-gray-50 transition">
        <div className="flex items-start justify-between gap-3">
          <div>
            <span className={`inline-block text-[10px] font-semibold border px-1.5 py-0.5 rounded-full mb-1.5 ${cat.cls}`}>
              {cat.label}
            </span>
            <p className="text-sm font-semibold text-gray-800">{index + 1}. {q.question}</p>
            <p className="text-xs text-gray-400 mt-1">Triggered by: “{q.source}”</p>
          </div>
          {open ? <ChevronUp className="w-4 h-4 text-gray-400 flex-shrink-0 mt-1" /> : <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0 mt-1" />}
        </div>
      </button>
      {open && (
        <div className="px-4 pb-4 pt-1 bg-emerald-50/50 border-t border-gray-100">
          <p className="text-xs font-semibold text-emerald-700 mb-1">Suggested STAR talking point</p>
          <p className="text-sm text-gray-700 leading-relaxed">{q.star_answer}</p>
        </div>
      )}
    </div>
  );
}

function RedFlagCard({ flag }) {
  const copyLine = () => {
    navigator.clipboard?.writeText(flag.neutralizing_line);
    toast.success('Line copied, paste it into your summary or cover note.');
  };
  return (
    <div className="border border-gray-200 rounded-xl bg-white px-4 py-3">
      <div className="flex items-start justify-between gap-3">
        <p className="text-sm font-semibold text-gray-800">{flag.objection}</p>
        <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full flex-shrink-0 ${SEVERITY_CLS[flag.severity] || SEVERITY_CLS.low}`}>
          {flag.severity}
        </span>
      </div>
      <p className="text-xs text-gray-400 mt-1">{flag.evidence}</p>
      <div className="mt-2.5 bg-blue-50 border border-blue-100 rounded-lg px-3 py-2">
        <div className="flex items-start justify-between gap-2">
          <div>
            <p className="text-[10px] font-semibold text-blue-500 uppercase mb-0.5">
              Get ahead of it, drop into your {String(flag.where_to_use || 'summary').replace('_', ' ')}
            </p>
            <p className="text-sm text-gray-700">“{flag.neutralizing_line}”</p>
          </div>
          <button onClick={copyLine} title="Copy line" className="text-blue-400 hover:text-blue-600 transition flex-shrink-0">
            <Copy className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Interview Prep, "Defend Your Resume". One click after the ATS resume is
 * generated: predicts the questions this resume will draw (with STAR talking
 * points) and the objections likely to sink it (with neutralizing lines).
 */
export default function InterviewPrep() {
  const [prep, setPrep] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState('questions');

  const fetchPrep = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${api}interview-prep/`, {}, { withCredentials: true });
      setPrep(res.data);
    } catch {
      toast.error('Could not build interview prep. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!prep) {
    return (
      <button
        onClick={fetchPrep}
        disabled={loading}
        className="flex items-center gap-2 px-5 py-2.5 bg-indigo-600 text-white rounded-xl font-semibold hover:bg-indigo-500 disabled:opacity-50 transition text-sm"
      >
        {loading ? <Loader className="animate-spin w-4 h-4" /> : <HelpCircle className="w-4 h-4" />}
        {loading ? 'Reading like a recruiter…' : 'Interview Prep'}
      </button>
    );
  }

  const questions = Array.isArray(prep.questions) ? prep.questions : [];
  const redFlags = Array.isArray(prep.red_flags) ? prep.red_flags : [];

  return (
    <div className="w-full bg-white border border-indigo-200 rounded-2xl p-5 shadow-sm">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-bold text-gray-800">Defend Your Resume</h3>
        <p className="text-xs text-gray-400">What happens after you hit “apply”</p>
      </div>
      <div className="flex gap-2 mb-4">
        <button
          onClick={() => setTab('questions')}
          className={`flex-1 py-2 rounded-xl text-sm font-medium transition ${tab === 'questions' ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
        >
          <span className="inline-flex items-center gap-1.5"><HelpCircle className="w-3.5 h-3.5" /> Likely Questions ({questions.length})</span>
        </button>
        <button
          onClick={() => setTab('flags')}
          className={`flex-1 py-2 rounded-xl text-sm font-medium transition ${tab === 'flags' ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
        >
          <span className="inline-flex items-center gap-1.5"><AlertTriangle className="w-3.5 h-3.5" /> Objection Radar ({redFlags.length})</span>
        </button>
      </div>
      <div className="space-y-2.5 max-h-[560px] overflow-y-auto pr-1">
        {tab === 'questions'
          ? questions.map((q, i) => <QuestionCard key={i} q={q} index={i} />)
          : redFlags.map((f, i) => <RedFlagCard key={i} flag={f} />)}
        {tab === 'questions' && questions.length === 0 && (
          <p className="text-sm text-gray-400 text-center py-6">No questions predicted.</p>
        )}
        {tab === 'flags' && redFlags.length === 0 && (
          <p className="text-sm text-gray-400 text-center py-6">No red flags found, clean pairing.</p>
        )}
      </div>
      <p className="text-[11px] text-gray-400 mt-3">
        Make sure you can defend every flagged bullet, these are the claims a screener is most likely to probe.
      </p>
    </div>
  );
}
